import { addDays, endOfWeek, isMonday as isMondayFn, parseISO, set, startOfWeek } from 'date-fns'
import { makeAutoObservable, runInAction } from 'mobx'

import { homeApi } from '../@common/api'
import type { DailyWorksheetItem } from '../@common/response'

export default class DailyLearningService {
  dailyLearning: DailyWorksheetItem[] = []

  constructor() {
    makeAutoObservable(this)
  }

  get isAllNotStarted() {
    return this.dailyLearning.every((day) => day.status === 'NOT_STARTED')
  }

  // 학습일 기준은 자정이 아니라 오전 2시부터 시작, 사용자가 오전 2시 이전에 한 학습은 전날 학습으로 간주
  // 오늘의 학습(todayLearning)은 현재 시각이 이 학습 구간에 속하는 day를 반환
  get todayLearning() {
    const now = new Date()

    const isTodayLearningRange = (dateString: string) => {
      const baseDate = parseISO(dateString)

      const learningStartTime = set(baseDate, { hours: 2, minutes: 0, seconds: 0, milliseconds: 0 }) // 당일 오전 2시
      const learningEndTime = addDays(learningStartTime, 1) // 다음날 오전 2시

      return now >= learningStartTime && now < learningEndTime
    }
    return this.dailyLearning.find((day) => isTodayLearningRange(day.date))
  }

  get consecutiveDailyLearningCount() {
    const todayItem = this.todayLearning
    if (!todayItem) return 0

    const todayIndex = this.dailyLearning.findIndex((day) => day.date === todayItem.date)
    if (todayIndex === -1) return 0

    let count = 0
    // 오늘부터 이전으로 거슬러 올라가면서 연속된 COMPLETED 또는 IN_PROGRESS 개수 세기
    for (let i = todayIndex; i >= 0; i--) {
      const item = this.dailyLearning[i]
      if (item.status === 'COMPLETED' || item.status === 'IN_PROGRESS') {
        count++
      } else {
        break // 연속이 끊기면 중단
      }
    }
    return count
  }

  // 데이터트래킹 용 - 최대 연속 학습 일자
  get maxConsecutiveWeeklyLearningCount() {
    // 현재 주의 월요일부터 일요일까지 데이터 필터링
    const today = new Date()
    const weekStart = startOfWeek(today, { weekStartsOn: 1 }) // 월요일 시작
    const weekEnd = endOfWeek(today, { weekStartsOn: 1 }) // 일요일 끝

    const currentWeekItems = this.dailyLearning
      .filter((item) => {
        const itemDate = new Date(item.date)
        return itemDate >= weekStart && itemDate <= weekEnd
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()) // 날짜 순 정렬

    if (currentWeekItems.length === 0) return 0

    // 주 내에서 가장 긴 연속 학습 기간 찾기
    let maxCount = 0
    let currentCount = 0

    for (const item of currentWeekItems) {
      if (item.status === 'COMPLETED' || item.status === 'IN_PROGRESS') {
        currentCount++
        maxCount = Math.max(maxCount, currentCount)
      } else {
        currentCount = 0
      }
    }

    return maxCount
  }

  get isConsecutiveDailyLearning() {
    return this.consecutiveDailyLearningCount > 1
  }

  get isMonday() {
    return isMondayFn(new Date())
  }

  async loadDailyLearning(studentId: string) {
    const data = await homeApi.fetchTodayLearning(studentId)
    runInAction(() => {
      this.dailyLearning = data
    })
  }

  async startDailyLearning(studentId: string) {
    const studentWorksheetId = await homeApi.postDailyWorksheet(studentId)
    return studentWorksheetId
  }
}
